import { useState, useEffect, useCallback, useRef } from 'react'
import { sessionApi } from '@services/api'
import type { SessionTimeRemaining } from '@/types'

// Configurazione heartbeat e warning
const HEARTBEAT_INTERVAL_MS = 60000 // Rinnova ogni 60 secondi durante attività
const WARNING_THRESHOLD_SECONDS = 120 // Mostra warning 2 minuti prima della scadenza
const CHECK_INTERVAL_MS = 10000 // Controlla tempo rimanente ogni 10 secondi

interface UseSessionOptions {
  mode?: 'self' | 'operator'
  onSessionExpired?: () => void
  onWarningShow?: (secondsRemaining: number) => void
  onWarningHide?: () => void
}

interface UseSessionReturn {
  sessionId: string | null
  isLoading: boolean
  error: string | null
  mode: 'self' | 'operator'
  timeRemaining: SessionTimeRemaining | null
  isExpired: boolean
  showWarning: boolean
  secondsToExpiry: number
  refresh: () => Promise<void>
  renewSession: () => Promise<boolean>
  endSession: () => Promise<void>
}

export function useSession(options: UseSessionOptions = {}): UseSessionReturn {
  const { 
    mode = 'self', 
    onSessionExpired, 
    onWarningShow,
    onWarningHide 
  } = options
  
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<SessionTimeRemaining | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [secondsToExpiry, setSecondsToExpiry] = useState(0)
  
  // Refs per gestire gli intervalli
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const isRenewingRef = useRef<boolean>(false)
  
  /**
   * Inizializza la sessione
   */
  const initSession = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      setIsExpired(false)
      setShowWarning(false)
      
      const response = await sessionApi.create(mode)
      
      setSessionId(response.session_id)
      setTimeRemaining(response.time_remaining)
      setSecondsToExpiry(response.time_remaining.seconds)
      
      // Resetta last activity
      lastActivityRef.current = Date.now()
      
    } catch (err) {
      console.error('Session init error:', err)
      setError('Impossibile inizializzare la sessione')
      
      // Genera un session ID temporaneo locale (fallback)
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      setSessionId(tempId)
    } finally {
      setIsLoading(false)
    }
  }, [mode])
  
  /**
   * Rinnova la sessione (heartbeat)
   */
  const renewSession = useCallback(async (): Promise<boolean> => {
    if (!sessionId || sessionId.startsWith('temp-') || isRenewingRef.current) {
      return false
    }
    
    try {
      isRenewingRef.current = true
      
      const response = await sessionApi.renew(sessionId)
      
      if (response.renewed) {
        setTimeRemaining(response.time_remaining)
        setSecondsToExpiry(response.time_remaining.seconds)
        setShowWarning(false)
        lastActivityRef.current = Date.now()
        onWarningHide?.()
        return true
      }
      
      return false
    } catch (err: unknown) {
      // Verifica se è un errore di sessione scaduta
      const apiError = err as { statusCode?: number }
      if (apiError?.statusCode === 401) {
        setIsExpired(true)
        setShowWarning(false)
        onSessionExpired?.()
      }
      return false
    } finally {
      isRenewingRef.current = false
    }
  }, [sessionId, onSessionExpired, onWarningHide])
  
  /**
   * Controlla il tempo rimanente (senza rinnovare)
   */
  const checkTimeRemaining = useCallback(async () => {
    if (!sessionId || sessionId.startsWith('temp-') || isExpired) {
      return
    }
    
    try {
      const remaining = await sessionApi.getTimeRemaining(sessionId)
      
      setTimeRemaining(remaining)
      setSecondsToExpiry(remaining.seconds)
      
      if (remaining.expired) {
        setIsExpired(true)
        setShowWarning(false)
        onSessionExpired?.()
        return
      }
      
      // Mostra warning se sotto la soglia
      if (remaining.seconds <= WARNING_THRESHOLD_SECONDS && !showWarning) {
        setShowWarning(true)
        onWarningShow?.(remaining.seconds)
      } else if (remaining.seconds > WARNING_THRESHOLD_SECONDS && showWarning) {
        setShowWarning(false)
        onWarningHide?.()
      }
      
    } catch (err: unknown) {
      const apiError = err as { statusCode?: number }
      if (apiError?.statusCode === 401) {
        setIsExpired(true)
        setShowWarning(false)
        onSessionExpired?.()
      }
    }
  }, [sessionId, isExpired, showWarning, onSessionExpired, onWarningShow, onWarningHide])
  
  /**
   * Termina la sessione volontariamente
   */
  const endSession = useCallback(async () => {
    if (!sessionId || sessionId.startsWith('temp-')) {
      return
    }
    
    try {
      await sessionApi.end(sessionId)
    } catch {
      // Ignora errori di terminazione
    }
    
    setIsExpired(true)
    setShowWarning(false)
    setSessionId(null)
    setTimeRemaining(null)
  }, [sessionId])
  
  /**
   * Refresh completo della sessione
   */
  const refresh = useCallback(async () => {
    await initSession()
  }, [initSession])
  
  /**
   * Gestisce l'attività utente per il heartbeat
   */
  const handleUserActivity = useCallback(() => {
    const now = Date.now()
    const timeSinceLastActivity = now - lastActivityRef.current
    
    // Rinnova solo se è passato abbastanza tempo dall'ultima attività
    if (timeSinceLastActivity >= HEARTBEAT_INTERVAL_MS - 5000) {
      lastActivityRef.current = now
      renewSession()
    }
  }, [renewSession])
  
  // Inizializza sessione al mount
  useEffect(() => {
    initSession()
    
    return () => {
      // Cleanup degli intervalli al unmount
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
    }
  }, [initSession])
  
  // Imposta intervalli per check e heartbeat
  useEffect(() => {
    if (!sessionId || sessionId.startsWith('temp-') || isExpired) {
      return
    }
    
    // Intervallo per controllare tempo rimanente
    checkIntervalRef.current = setInterval(checkTimeRemaining, CHECK_INTERVAL_MS)
    
    // Cleanup
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
    }
  }, [sessionId, isExpired, checkTimeRemaining])
  
  // Ascolta eventi di attività utente per heartbeat
  useEffect(() => {
    if (!sessionId || sessionId.startsWith('temp-') || isExpired) {
      return
    }
    
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true })
    })
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity)
      })
    }
  }, [sessionId, isExpired, handleUserActivity])
  
  // Countdown locale per UI più fluida
  useEffect(() => {
    if (isExpired || !timeRemaining || timeRemaining.expired) {
      return
    }
    
    const countdownInterval = setInterval(() => {
      setSecondsToExpiry(prev => {
        const newValue = Math.max(0, prev - 1)
        
        // Controlla se dovremmo mostrare warning
        if (newValue <= WARNING_THRESHOLD_SECONDS && newValue > 0 && !showWarning) {
          setShowWarning(true)
          onWarningShow?.(newValue)
        }
        
        // Controlla se scaduto
        if (newValue === 0) {
          setIsExpired(true)
          setShowWarning(false)
          onSessionExpired?.()
        }
        
        return newValue
      })
    }, 1000)
    
    return () => clearInterval(countdownInterval)
  }, [isExpired, timeRemaining, showWarning, onWarningShow, onSessionExpired])
  
  return {
    sessionId,
    isLoading,
    error,
    mode,
    timeRemaining,
    isExpired,
    showWarning,
    secondsToExpiry,
    refresh,
    renewSession,
    endSession
  }
}
